/**
 * Makeup Lab layer shape — matches Makeup Studio controls for future CV merge.
 * Color categories: colorName, color (hex), intensity 0–100, finishType/finishName.
 * Eyeliner / eyelash: template id + styleName (studio template name).
 */

export const LAB_CATEGORY_ORDER = [
  'foundation',
  'concealer',
  'blush',
  'eyeshadow',
  'eyeliner',
  'eyelash',
  'lipstick',
  'highlighter',
  'contour',
  'eyebrows',
];

const CATEGORY_LABELS = {
  foundation: 'Foundation',
  concealer: 'Concealer',
  blush: 'Blush',
  eyeshadow: 'Eyeshadow',
  eyeliner: 'Eyeliner',
  eyelash: 'Eyelash',
  lipstick: 'Lipstick',
  highlighter: 'Highlighter',
  contour: 'Contour',
  eyebrows: 'Brows',
};

export function colorLayer(colorName, color, intensity, finish = {}) {
  return {
    colorName,
    color: color || null,
    intensity: Math.round(intensity),
    ...(finish.finishType ? { finishType: finish.finishType } : {}),
    ...(finish.finishName ? { finishName: finish.finishName } : {}),
  };
}

export function skinMatchLayer(intensity, finish = {}) {
  return { ...colorLayer('(from skin analysis)', null, intensity, finish), autoSkin: true };
}

export function autoSkinConcealerLayer(intensity, finish = {}) {
  return { ...colorLayer('(from skin analysis)', null, intensity, finish), autoSkinConcealer: true };
}

export function noneLayer() {
  return { colorName: 'None', color: null, intensity: 0, disabled: true };
}

/** Every category explicit — missing preset slots default to None. */
export function initialCustomLabForPreset(presetLab) {
  const out = {};
  for (const cat of LAB_CATEGORY_ORDER) {
    const layer = presetLab?.[cat];
    out[cat] = layer && typeof layer === 'object' ? { ...layer } : noneLayer();
  }
  return out;
}

export function isLayerActive(layer) {
  if (!layer || typeof layer !== 'object') return false;
  if (layer.disabled || layer.none) return false;
  if (String(layer.colorName || '').trim().toLowerCase() === 'none') return false;
  return Number(layer.intensity) > 0;
}

/** @param {string} styleName - Studio template display name (e.g. Cat Eye) */
export function eyelinerLayer(styleName, template, colorName, color, intensity) {
  return {
    styleName,
    template,
    colorName,
    color,
    intensity: Math.round(intensity),
  };
}

export function eyelashLayer(styleName, template, colorName, color, intensity) {
  return {
    styleName,
    template,
    colorName,
    color,
    intensity: Math.round(intensity),
  };
}

function formatLayerLine(categoryId, layer) {
  const label = CATEGORY_LABELS[categoryId] || categoryId;
  const pct = `${layer.intensity}%`;
  let colorPart =
    layer.colorName && layer.color
      ? `${layer.colorName} (${layer.color})`
      : layer.colorName || (layer.color ? layer.color : 'match guest');
  const finishPart = layer.finishName ? `, ${layer.finishName} finish` : '';

  if ((categoryId === 'foundation' || categoryId === 'concealer') && layer.color && layer.colorName) {
    colorPart = `${layer.colorName} (${layer.color})`;
  }

  if (categoryId === 'eyeliner' || categoryId === 'eyelash') {
    const stylePart = layer.styleName ? `style "${layer.styleName}"` : 'style natural';
    const tpl = layer.template ? `, template ${layer.template}` : '';
    return `${label}: ${stylePart}, ${colorPart}, strength ${pct}${tpl}`;
  }

  return `${label}: ${colorPart}, strength ${pct}${finishPart}`;
}

export function foundationEmphasisLine(makeupLab) {
  const parts = [];
  for (const key of ['foundation', 'concealer']) {
    const layer = makeupLab?.[key];
    if (!isLayerActive(layer)) continue;
    parts.push(formatLayerLine(key, layer));
  }
  if (!parts.length) return '';
  const fHex = makeupLab?.foundation?.color;
  const depthNote = fHex
    ? ` Mandatory hex ${fHex} on face — same depth as neck; never whiten or lift tone.`
    : ' Match guest depth; face same tone as neck; never whiten.';
  return (
    `BASE / FOUNDATION: ${parts.join('; ')}. ` +
    'Sheer real-world base on face only — feather jaw; keep pores and natural shine.' +
    depthNote
  );
}

export function lipstickEmphasisLine(makeupLab) {
  const layer = makeupLab?.lipstick;
  if (!isLayerActive(layer)) return '';
  const detail = formatLayerLine('lipstick', layer);
  return (
    `LIPSTICK: ${detail}. Soft real-world color inside the natural lip line; keep lip shape unchanged.`
  );
}

export const PROMPT_FOUNDATION_GUARD =
  'COMPLEXION (MANDATORY): Apply listed foundation hex on face at stated strength — exact depth match to guest skin; face must not be lighter than neck; keep pores and natural shine.';

export const PROMPT_SKIN_MICRODETAIL =
  'SKIN MICRODETAIL (MANDATORY): Preserve every mole, freckle, beauty mark, bindi, and skin grain exactly as in the upload — makeup sits on top; never erase or blur fine detail.';

export const PROMPT_NO_BLUSH_GUARD =
  'NO BLUSH (MANDATORY): Blush is not listed — cheek apples must match bare skin in the upload; no pink/rose/coral disks; highlighter and contour are not blush.';

export const PROMPT_REALISM_GUARDS = [
  'REALISM (MANDATORY): Photoreal in-place edit — sheer makeup, visible pores, undertone unchanged. No plastic skin, no beauty filter.',
  'BLUSH: Soft cheek wash only — no neon disks, no cheek spotlight.',
  'EYES: Lid/crease only — no pink brow-bone wash, no raccoon mask.',
];

function hasActiveBase(makeupLab) {
  return ['foundation', 'concealer'].some((key) => isLayerActive(makeupLab?.[key]));
}

function hasActiveBlush(makeupLab) {
  return isLayerActive(makeupLab?.blush);
}

function hasActiveEyes(makeupLab) {
  return ['eyeshadow', 'eyeliner', 'eyelash'].some((key) => isLayerActive(makeupLab?.[key]));
}

export function isLipOnlyMinimalLab(makeupLab) {
  if (!isLayerActive(makeupLab?.lipstick)) return false;
  for (const key of [
    'foundation',
    'concealer',
    'blush',
    'eyeshadow',
    'eyeliner',
    'eyelash',
    'highlighter',
    'contour',
  ]) {
    if (isLayerActive(makeupLab?.[key])) return false;
  }
  const brow = makeupLab?.eyebrows;
  if (isLayerActive(brow) && brow.color) return false;
  return true;
}

export const CUSTOM_MAKEUP_LAB_HEADER =
  'CUSTOM MAKEUP LAB (authoritative — apply only these values):';

export function buildLookPromptFromLab(
  lookId,
  lookLabel,
  makeupLab,
  styleLine,
  guardsLine,
  skinToneBlock,
  complexionLock,
  lipOnlyMode = false,
  customAuthoritative = false,
) {
  const lines = customAuthoritative
    ? [
        `LOOK: custom-makeup (UI preset "${lookLabel}" / ${lookId} — reference only)`,
        '',
        'PRESET DEFAULTS DISABLED: Do not apply the named preset look. Follow CUSTOM MAKEUP LAB and skin blocks only.',
        '',
      ]
    : [`LOOK: ${lookId} (${lookLabel})`, ''];
  if (skinToneBlock) {
    lines.push(skinToneBlock, '');
  }
  if (complexionLock) {
    lines.push(complexionLock, '');
  }
  lines.push(customAuthoritative ? CUSTOM_MAKEUP_LAB_HEADER : 'MAKEUP LAB (apply only these values):');
  for (const key of LAB_CATEGORY_ORDER) {
    const layer = makeupLab?.[key];
    if (!isLayerActive(layer)) continue;
    lines.push(formatLayerLine(key, layer));
  }
  const fndLine = foundationEmphasisLine(makeupLab);
  if (fndLine) {
    lines.push(fndLine);
  }
  const lipLine = lipstickEmphasisLine(makeupLab);
  if (lipLine) {
    lines.push(lipLine);
  }
  lines.push(
    'APPLICATION: Only categories listed above — subtle real-world strength; never theatrical or filter-like.',
  );
  lines.push(PROMPT_REALISM_GUARDS[0]);
  if (hasActiveBase(makeupLab)) {
    lines.push(PROMPT_FOUNDATION_GUARD);
  }
  if (!lipOnlyMode && hasActiveBlush(makeupLab)) {
    lines.push(PROMPT_REALISM_GUARDS[1]);
  } else if (!lipOnlyMode) {
    lines.push(PROMPT_NO_BLUSH_GUARD);
  }
  if (!lipOnlyMode && hasActiveEyes(makeupLab)) {
    lines.push(PROMPT_REALISM_GUARDS[2]);
  }
  if (lipOnlyMode) {
    lines.push(
      'SKIN UNCHANGED (MANDATORY): Cheeks, forehead, nose, chin, and neck must be pixel-faithful to the upload — real pores, melanin, and natural shine; no filter.',
    );
  }
  if (!hasActiveBase(makeupLab)) {
    lines.push(
      'NO BASE MAKEUP (MANDATORY): Do not apply foundation, concealer, powder, skin tint, or beauty-filter smoothing — natural photographed skin only.',
    );
    lines.push(
      'FACE DEPTH LOCK: Cheek and forehead luminosity must match neck and décolletage — no whitening, no uniform flat base, no mask edge at jaw.',
    );
  }
  if (styleLine) {
    lines.push('', `STYLE: ${String(styleLine).trim()}`);
  }
  if (guardsLine) {
    lines.push(String(guardsLine).trim());
  }
  lines.push(PROMPT_SKIN_MICRODETAIL);
  lines.push('', PROMPT_PRESERVE_BLOCK);
  return lines.join('\n');
}

export const PROMPT_PRESERVE_BLOCK = `PRESERVE (MANDATORY):
- Exact facial identity: same person, bone structure, proportions, expression, and apparent age
- Same eye shape, nose, jaw, and eyebrows — do not reshape or enlarge features
- Same lip shape, outline, and size; lipstick color from MAKEUP LAB must still be applied and visible
- Same head pose, head tilt, gaze direction, and camera framing — no zoom, crop, or angle change
- Same lighting as the original photo: direction, softness, shadows, highlights, and color temperature — no relighting, flash swap, or studio relight
- Same background, environment, hair, hairline, bindi, jewelry, clothing, and accessories
- Skin depth and undertone on the face must match neck, ears, and visible chest — never lighten, whiten, pink-shift, or grey-cast
- Keep natural skin texture: pores, fine lines, peach fuzz, and subtle imperfections visible
- Keep every mole, freckle, beauty mark, bindi, and small facial mark in the same place, size, and contrast as the upload
- No ghosting, duplicate features, or semi-transparent makeup mask — single coherent face only
- Do not beautify, de-age, or replace the face; in-place edit only`;
